import { Link } from 'react-router-dom'
import { Badge } from '../../components/Badge'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ToastProvider, useToast } from '../../components/Toast'
import { useDb } from '../../state/DbProvider'
import { findProduct, findUser, getCurrentUser } from '../../state/selectors'
import { formatDateYMD, formatJPY, formatPercent } from '../../utils/format'

function AdminDealsInner() {
  const { db, actions } = useDb()
  const toast = useToast()

  if (!db) return null
  const me = getCurrentUser(db)

  if (me.role !== 'J-Navi管理者') {
    return (
      <Card title="商談管理" subtitle="このページは J-Navi 管理者向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const openDeals = db.deals.filter((deal) => !deal.locked && deal.status !== '失注')
  const transactions = [...db.transactions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))

  return (
    <div className="space-y-6">
      <Card title="進行中の商談" subtitle="代理店直登録とコネクター経由登録を含む、現在進行中の商談一覧です。">
        <div className="overflow-x-auto px-5 py-4">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">商品</th>
                <th className="px-4 py-3">顧客</th>
                <th className="px-4 py-3">ステータス</th>
                <th className="px-4 py-3">コネクター</th>
                <th className="px-4 py-3">担当先</th>
              </tr>
            </thead>
            <tbody>
              {openDeals.map((deal) => {
                const product = findProduct(db, deal.productId)
                const connector = findUser(db, deal.connectorId)
                const agency = deal.assignedAgencyId
                  ? findUser(db, deal.assignedAgencyId)
                  : connector?.connector?.agencyId
                    ? findUser(db, connector.connector.agencyId)
                    : undefined

                return (
                  <tr key={deal.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{product?.name ?? deal.productId}</div>
                      <div className="text-xs text-slate-500">{formatDateYMD(deal.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{deal.customerCompanyName || '会社名未入力'}</div>
                      <div className="text-xs text-slate-500">{deal.customerName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone="amber">{deal.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{connector?.name ?? 'なし'}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{agency?.name ?? '-'}</div>
                      <div className="text-xs text-slate-500">{agency?.role ?? ''}</div>
                    </td>
                  </tr>
                )
              })}
              {openDeals.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={5}>
                    進行中の商談はありません。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="売上確定済み" subtitle="顧客からの入金を確認できた案件だけ、運営が報酬を確定します。">
        <div className="overflow-x-auto px-5 py-4">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">商品 / 商談</th>
                <th className="px-4 py-3">売上金額</th>
                <th className="px-4 py-3">総代理店</th>
                <th className="px-4 py-3">代理店</th>
                <th className="px-4 py-3">コネクター</th>
                <th className="px-4 py-3">J-Navi</th>
                <th className="px-4 py-3">状態</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => {
                const agency = findUser(db, transaction.agencyId)
                const superAgencyAllocation = transaction.allocations.find(
                  (allocation) => allocation.recipientType !== 'Jnavi取り分' && allocation.userRole === '総代理店',
                )
                const superAgency = superAgencyAllocation?.userId
                  ? findUser(db, superAgencyAllocation.userId)
                  : undefined
                const connector = findUser(db, transaction.connectorId)
                const userAllocations = transaction.allocations.filter((allocation) => allocation.recipientType !== 'Jnavi取り分')
                const hasPending = userAllocations.some((allocation) => 'status' in allocation && allocation.status === '未確定')
                const agencyRate = transaction.connectorId
                  ? Math.max(0, transaction.ratesUsed.agencyRate - transaction.ratesUsed.connectorRate)
                  : transaction.ratesUsed.agencyRate

                return (
                  <tr key={transaction.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{transaction.productSnapshot.name}</div>
                      <div className="text-xs text-slate-500">
                        商談 ID: {transaction.dealId} / 確定日: {formatDateYMD(transaction.closingDate)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{formatJPY(transaction.saleAmountJPY)}</div>
                      <div className="text-xs text-slate-500">
                        {superAgencyAllocation ? `総代理店 ${formatPercent(superAgencyAllocation.rate ?? 0)} / ` : ''}
                        代理店 {formatPercent(agencyRate)}
                        {transaction.connectorId ? ` / コネクター ${formatPercent(transaction.ratesUsed.connectorRate)}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{formatJPY(superAgencyAllocation?.amountJPY ?? transaction.superAgencyRewardJPY ?? 0)}</div>
                      <div className="mt-1 text-xs text-slate-500">{superAgency?.name ?? 'なし'}</div>
                    </td>
                    <td className="px-4 py-3">{formatJPY(transaction.agencyRewardJPY)}</td>
                    <td className="px-4 py-3">{formatJPY(transaction.connectorRewardJPY)}</td>
                    <td className="px-4 py-3">{formatJPY(transaction.jnaviShareJPY)}</td>
                    <td className="px-4 py-3">
                      {hasPending ? <Badge tone="amber">入金確認待ち</Badge> : <Badge tone="green">報酬確定済み</Badge>}
                      <div className="mt-1 text-xs text-slate-500">
                        代理店: {agency?.name ?? transaction.agencyId}
                        {superAgency ? ` / 総代理店: ${superAgency.name}` : ''}
                        {' / '}
                        コネクター: {connector?.name ?? 'なし'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        {hasPending ? (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={async () => {
                              const result = await actions.adminConfirmRewardsForTransaction(transaction.id)
                              if (!result.ok) {
                                toast.show(result.message ?? '報酬を確定できませんでした', 'error')
                                return
                              }
                              toast.show('報酬を確定し、代理店/コネクター側へ入金待ちとして反映しました', 'success')
                            }}
                          >
                            入金確認後に報酬確定
                          </Button>
                        ) : (
                          <Link to="/admin/logs" className="text-xs text-blue-600 hover:underline">
                            ログを見る
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {transactions.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={8}>
                    売上確定済みデータはまだありません。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

export default function AdminDealsPage() {
  return (
    <ToastProvider>
      <AdminDealsInner />
    </ToastProvider>
  )
}
